// BYO-01 "My subscriptions" (`/c/{slug}/subscriptions`, from the account menu): the caller's own
// AI keys and CLI login reference, their own tool connections, and their channel subscriptions.
// Each path shows why it is closed when the platform or the company has not opened it.
// SETUP-UX: the 56 px location header (Account › My subscriptions), the three tabs as its
// toolbar, then groups of rows; no intro paragraphs.
import { InfoTip, Notice, ViewTabs } from "@companyos/ui";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import type { Api } from "../../api/client";
import type { CompanyAccess } from "../company";
import { AccessMatrixScreen } from "../connections/access-matrix";
import { Catalog } from "../connections/catalog";
import { ConnectionRows } from "../connections/connection-rows";
import { LoadError, Loading } from "../screen-parts";
import { SetupPage } from "../settings/setup-page";
import { ChannelsTab } from "./channels-tab";
import { CliCard } from "./cli-card";
import { CredentialList, KeyForm } from "./parts";
import { connectionsQuery, credentialsQuery, type Policy, policyQuery } from "./queries";

// INC2-ACCESS: Access, the matrix of the agents you may see (your own connections editable for
// the DEs you supervise).
export const MY_SUBSCRIPTION_TABS = ["ai", "connections", "access", "channels"] as const;
export type MySubscriptionTab = (typeof MY_SUBSCRIPTION_TABS)[number];
const LABELS: Record<MySubscriptionTab, string> = {
  ai: "AI",
  connections: "Connections",
  access: "Access",
  channels: "Channels",
};

export function MySubscriptions({
  api,
  company,
  tab = "ai",
  onTab,
}: {
  api: Api;
  company: CompanyAccess;
  tab?: MySubscriptionTab;
  onTab: (t: MySubscriptionTab) => void;
}) {
  const policy = useQuery(policyQuery(api, company.id));
  return (
    <SetupPage
      crumbs={["Your settings"]}
      title="My subscriptions"
      toolbar={
        <div className="px-6 max-md:px-4">
          <ViewTabs
            className="min-w-0"
            aria-label="Subscription kinds"
            value={tab}
            onValueChange={(v) => onTab(v as MySubscriptionTab)}
            tabs={MY_SUBSCRIPTION_TABS.map((t) => ({ value: t, label: LABELS[t] }))}
          />
        </div>
      }
    >
      <div role="tabpanel" aria-label={LABELS[tab]} className="flex min-w-0 flex-col gap-4">
        {tab === "channels" ? (
          <ChannelsTab api={api} co={company.id} />
        ) : tab === "access" ? (
          <AccessMatrixScreen api={api} co={company.id} />
        ) : policy.isPending ? (
          <Loading
            label={tab === "ai" ? "Loading your subscriptions" : "Loading your connections"}
            rows={4}
          />
        ) : policy.isError ? (
          <LoadError
            what={tab === "ai" ? "Your subscriptions" : "Your connections"}
            error={policy.error}
          />
        ) : tab === "ai" ? (
          <AiTab api={api} company={company} policy={policy.data} />
        ) : (
          <ConnectionsTab api={api} company={company} policy={policy.data} />
        )}
      </div>
    </SetupPage>
  );
}

/** Why a path is closed: the founder's platform switch first, then the company's flag. */
export function ClosedNotice({
  platformOn,
  slug,
  canManage,
  what,
}: {
  platformOn: boolean;
  slug: string;
  canManage: boolean;
  what: string;
}) {
  return (
    <Notice tone="info" title={`${what} are off here`} data-testid="path-closed">
      {platformOn ? (
        canManage ? (
          <>
            Turn them on in{" "}
            <Link
              to="/c/$slug/agents/connections"
              params={{ slug }}
              className="underline underline-offset-2"
            >
              Team › Connections › Paths
            </Link>
            .
          </>
        ) : (
          "An owner or admin of this company can turn them on."
        )
      ) : (
        "CompanyOS has not opened this path yet; it is a platform decision, not this company's."
      )}
    </Notice>
  );
}

function AiTab({ api, company, policy }: { api: Api; company: CompanyAccess; policy: Policy }) {
  const q = useInfiniteQuery(credentialsQuery(api, company.id));
  const mine = (q.data?.pages.flatMap((p) => p.data) ?? []).filter((c) => c.scope === "person");
  const keysOpen = policy.effective.person_api_keys;
  return (
    <>
      {q.isPending ? (
        <Loading label="Loading your keys" rows={2} />
      ) : q.isError ? (
        <LoadError what="Your keys" error={q.error} />
      ) : (
        <CredentialList
          api={api}
          co={company.id}
          rows={mine}
          title="Your keys and logins"
          empty="No keys yet"
        >
          {keysOpen ? (
            <KeyForm api={api} co={company.id} scope="person" disabled={false} />
          ) : (
            <ClosedNotice
              what="Personal API keys"
              platformOn={policy.platform.person_api_keys}
              slug={company.slug}
              canManage={policy.can_manage}
            />
          )}
        </CredentialList>
      )}
      <CliCard
        api={api}
        co={company.id}
        open={policy.effective.person_cli_subscriptions}
        canManage={policy.can_manage}
      />
    </>
  );
}

function ConnectionsTab({
  api,
  company,
  policy,
}: {
  api: Api;
  company: CompanyAccess;
  policy: Policy;
}) {
  const q = useInfiniteQuery(connectionsQuery(api, company.id));
  const mine = (q.data?.pages.flatMap((p) => p.data) ?? []).filter((c) => c.scope === "person");
  const open = policy.effective.tool_connections;
  return (
    <>
      {q.isPending ? (
        <Loading label="Loading your connections" rows={2} />
      ) : q.isError ? (
        <LoadError what="Your connections" error={q.error} />
      ) : (
        // INC2-CONTACT (integrations F4): My connections — the same rows and drawer as the
        // company's; grants go only to named DEs (the personal agent's are on My agent).
        <section className="flex flex-col gap-2" aria-label="My connections">
          <h2 className="flex items-center gap-1 text-sm font-medium">
            My connections
            <InfoTip label="About my connections">
              Your own accounts. Only you see them and their grants; grant a DE by name to let it
              read or act through one.
            </InfoTip>
          </h2>
          <ConnectionRows
            api={api}
            co={company.id}
            slug={company.slug}
            rows={mine}
            label="My connections"
            empty="No tools connected"
          />
        </section>
      )}
      {open ? null : (
        <ClosedNotice
          what="Tool connections"
          platformOn
          slug={company.slug}
          canManage={policy.can_manage}
        />
      )}
      {/* Only once the connections are read: a pending or failed read is not "none connected" */}
      {q.isSuccess ? (
        <section className="flex flex-col gap-2" aria-label="Connect a tool">
          <h2 className="text-sm font-medium">Connect a tool</h2>
          <Catalog
            api={api}
            co={company.id}
            slug={company.slug}
            scope="person"
            connections={mine}
            disabled={!open}
          />
        </section>
      ) : null}
    </>
  );
}
